import { useCallback } from 'react';

import { deleteFileFromBrowserStorage, TrackerVideoEvent, VideoRecordingEvent } from '@bonusly/common-action-input';
import { useDispatch } from '@bonusly/events';
import { useAnalytics } from '@bonusly/who-am-i';

import { RichTextLexer, RichTextParser } from '../../../action-input/lib/parser';

interface UseVideoModalProps {
	isOpen: boolean;
	onClose: () => void;
	onOpen: () => void;
	recordedVideo: string | null;
	isRecording: boolean;
	recordingAttempts: number;
	resetRecordingState: () => void;
	confirmModalOnClose: () => void;
	confirmModalOnOpen: () => void;
	getSuggestedBonusReason: () => any;
	aiProcessingEnabled: boolean;
}

export const useVideoModal = ({
	isOpen,
	onClose,
	onOpen,
	recordedVideo,
	isRecording,
	recordingAttempts,
	resetRecordingState,
	confirmModalOnClose,
	confirmModalOnOpen,
	getSuggestedBonusReason,
	aiProcessingEnabled,
}: UseVideoModalProps) => {
	const { track } = useAnalytics();
	const dispatch = useDispatch();

	const handleVideoRecordingModalClose = useCallback(() => {
		track(TrackerVideoEvent.videoRecordingModalClose, {
			hasRecording: !!recordedVideo,
			isRecording,
			recordingAttempts,
		});
		deleteFileFromBrowserStorage();
		resetRecordingState();
		confirmModalOnClose();
		onClose();
	}, [recordedVideo, isRecording, recordingAttempts, resetRecordingState, confirmModalOnClose, onClose, track]);

	const handleVideoModalConfirmBeforeClose = useCallback(() => {
		if (isOpen) {
			!!recordedVideo || isRecording ? confirmModalOnOpen() : handleVideoRecordingModalClose();
		}
	}, [isOpen, recordedVideo, isRecording, confirmModalOnOpen, handleVideoRecordingModalClose]);

	const handleSubmit = useCallback(async () => {
		if (aiProcessingEnabled) {
			try {
				const suggestedBonusReason = await getSuggestedBonusReason();
				console.log('suggestedBonusReason', suggestedBonusReason);

				// Parse the string into structured content
				const tokens = new RichTextLexer(suggestedBonusReason).tokens();
				const parser = new RichTextParser(tokens);
				const structuredContent = parser.content();

				dispatch(VideoRecordingEvent.AttachToBonus, { structuredContent });
			} catch (error) {
				console.error('Error getting suggested bonus reason:', error);
				dispatch(VideoRecordingEvent.AttachToBonus);
			}
		} else {
			dispatch(VideoRecordingEvent.AttachToBonus);
		}
		track(TrackerVideoEvent.videoAttachToBonus, { recordingAttempts });
		onClose();
	}, [dispatch, track, recordingAttempts, onClose, getSuggestedBonusReason, aiProcessingEnabled]);

	return {
		handleSubmit,
		handleVideoRecordingModalClose,
		handleVideoModalConfirmBeforeClose,
		isOpen,
		onOpen,
	};
};
